import React, { useEffect, useState, useRef } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import 'pdfjs-dist/build/pdf.worker.entry';
import './Pdfviewer.css';
import Swal from 'sweetalert2';
const PdfViewer = ({ src, label, colorClass, onScroll, scrollContainerRef, showSearch = true }) => {
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);
  const [searchInput, setSearchInput] = useState('');
  const canvasMapRef = useRef([]);
  const [dynamicPlaceholder, setDynamicPlaceholder] = useState('Go to...(e.g., 3A)');
  useEffect(() => {
    const fileName = src.split('/').pop()?.replace('.pdf', '');
    const rangeMatch = fileName?.match(/(\d+)-(\d+)/);

    if (rangeMatch) {
      const start = parseInt(rangeMatch[1], 10);
      const end = parseInt(rangeMatch[2], 10);
      const mid = Math.floor((start + end) / 2);
      setDynamicPlaceholder(`Go to...(e.g., ${mid}A)`);
    } else {
      setDynamicPlaceholder('Go to...(e.g., 3A)');
    }
  }, [src]);


  useEffect(() => {
    const renderPdf = async () => {
      setLoading(true);
      try {
        const loadingTask = pdfjsLib.getDocument(src);
        const pdf = await loadingTask.promise;

        const canvases = [];
        canvasMapRef.current = [];

        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 1.5 });

          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          canvas.width = viewport.width;
          canvas.height = viewport.height;

          await page.render({ canvasContext: context, viewport }).promise;

          canvas.className = 'pdf-canvas';
          canvas.style.marginBottom = '20px';
          canvas.style.display = 'block';

          canvases.push({ index: i, canvas });
          canvasMapRef.current.push(canvas);
        }

        const container = containerRef.current;
        container.innerHTML = '';
        canvases
          .sort((a, b) => a.index - b.index)
          .forEach(({ canvas }) => container.appendChild(canvas));

        setLoading(false);
      } catch (error) {
        console.error('Error rendering PDF:', error);
        setLoading(false);
      }
    };

    renderPdf();
  }, [src]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) container.scrollTop = 0;
  }, [src, scrollContainerRef]);

  const handleSearch = () => {
    let input = searchInput.trim().toLowerCase();

    if (/^\d+$/.test(input)) {
      input += 'a';
      setSearchInput(input.toUpperCase());
    }

    const match = input.match(/^(\d+)([ab])$/);
    if (!match) {
      Swal.fire({
        icon: 'info',
        title: 'Try format like 36A',
        text: 'Please use a format like 36A or 36B to jump to a page.',
      });
      return;
    }

    const requestedNum = parseInt(match[1], 10);
    const part = match[2];

    const fileName = src.split('/').pop()?.replace('.pdf', '');
    const rangeMatch = fileName?.match(/(\d+)-(\d+)/);

    if (!rangeMatch) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid file name format',
        text: 'Could not determine page range from filename.',
      });
      return;
    }

    const startNum = parseInt(rangeMatch[1], 10);
    const endNum = parseInt(rangeMatch[2], 10);

    if (requestedNum < startNum || requestedNum > endNum) {
      Swal.fire({
        icon: 'warning',
        title: 'Page Not Found',
        text: `Page ${requestedNum}${part.toUpperCase()} is not in this booklet. Try ${startNum}A–${endNum}B.`,
      });
      return;
    }

    const pageIndex = (requestedNum - startNum) * 2 + (part === 'a' ? 0 : 1);
    const targetCanvas = canvasMapRef.current[pageIndex];
    const scrollContainer = scrollContainerRef.current;

    if (targetCanvas && scrollContainer) {
      const headerRow = scrollContainer.querySelector('.pdf-label-row');
      const headerHeight = headerRow ? headerRow.offsetHeight : 60;

      // compute final position using offsetTop (works on desktop + mobile)
      const targetY = targetCanvas.offsetTop - headerHeight;

      // animate with requestAnimationFrame
      const startY = scrollContainer.scrollTop;
      const distance = targetY - startY;
      const duration = 400; // ms
      let startTime = null;

      const animateScroll = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // easeInOutQuad for smoothness
        const ease = progress < 0.5
          ? 2 * progress * progress
          : -1 + (4 - 2 * progress) * progress;

        scrollContainer.scrollTop = startY + distance * ease;

        if (elapsed < duration) {
          requestAnimationFrame(animateScroll);
        }
      };

      requestAnimationFrame(animateScroll);

      setSearchInput('');
    }
    else {
      console.error('Scroll failed:', { targetCanvas, scrollContainer, pageIndex });
      Swal.fire({
        icon: 'error',
        title: 'Canvas not found',
        text: `Page ${requestedNum}${part.toUpperCase()} could not be scrolled to.`,
      });
    }


  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  return (
    <div
      className={`pdf-scroll-view ${colorClass}`}
      onScroll={(e) => onScroll(e.target.scrollTop)}
      ref={scrollContainerRef}
    >
      <div className="pdf-label-row">
        <div className="pdf-label">{label}</div>

        {showSearch && (
          <div className="pdf-search-box">
            <input
              type="text"
              placeholder={dynamicPlaceholder}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleKeyPress}
            />
            <button onClick={handleSearch}>Go</button>
          </div>
        )}

      </div>

      {loading && <div className="loader">Loading...</div>}
      <div ref={containerRef} />
    </div>
  );
};

export default PdfViewer;
